// Live voice contour — the Cadenza signature interaction.
//
// Draws the melody (pitch over time) of whatever the user says, on a faint
// five-line staff, ending in the coral "cadence" dot from the logo. When no mic
// is attached it plays a synthetic, speech-like contour so the canvas is never
// empty.
//
// Everything runs in the browser: the audio is analysed frame by frame and
// thrown away. Nothing is recorded or uploaded by this module — callers that
// DO record (the app's practice flow) pass in their own MediaStream and keep
// ownership of it.
//
// This file is duplicated byte-for-byte in funnel/src/lib/voiceContour.js and
// frontend/src/lib/voiceContour.js (two separate builds, no shared package).
// Change both.

import { useCallback, useEffect, useRef, useState } from 'react'

const MIN_HZ = 65
const MAX_HZ = 500
const FRAMES_PER_SEC = 60
const VISIBLE_SECONDS = 4.5
const HEAD_X = 0.84 // where the newest point sits, as a fraction of width
const BRIDGE_FRAMES = 6 // unvoiced frames to bridge before the line breaks
const ST_SPAN = 9 // semitones above/below centre that fit in the drawing area

// Pitch by normalised autocorrelation on a 2x-downsampled window. Returns
// { hz, rms }; hz is 0 for silence or unvoiced sound.
export function detectPitch(raw, sampleRate) {
  const n = raw.length >> 1
  const buf = new Float32Array(n)
  let rms = 0
  for (let i = 0; i < n; i++) {
    const v = (raw[2 * i] + raw[2 * i + 1]) * 0.5
    buf[i] = v
    rms += v * v
  }
  rms = Math.sqrt(rms / n)
  if (rms < 0.01) return { hz: 0, rms }

  const sr = sampleRate / 2
  const minLag = Math.floor(sr / MAX_HZ)
  const maxLag = Math.min(Math.floor(sr / MIN_HZ), n - 2)
  const corr = new Float32Array(maxLag + 2)
  let gmax = 0
  for (let lag = minLag; lag <= maxLag + 1; lag++) {
    let s = 0
    let e1 = 0
    let e2 = 0
    for (let i = 0; i < n - lag; i++) {
      const a = buf[i]
      const b = buf[i + lag]
      s += a * b
      e1 += a * a
      e2 += b * b
    }
    const v = s / Math.sqrt(e1 * e2 + 1e-12)
    corr[lag] = v
    if (lag <= maxLag && v > gmax) gmax = v
  }
  if (gmax < 0.55) return { hz: 0, rms }

  // First peak close to the global max — taking the global max itself tends
  // to land an octave low.
  let best = -1
  for (let lag = minLag + 1; lag <= maxLag; lag++) {
    if (corr[lag] > 0.9 * gmax && corr[lag] >= corr[lag - 1] && corr[lag] >= corr[lag + 1]) {
      best = lag
      break
    }
  }
  if (best < 0) return { hz: 0, rms }
  const a = corr[best - 1]
  const b = corr[best]
  const c = corr[best + 1]
  const denom = a - 2 * b + c
  const shift = denom ? (0.5 * (a - c)) / denom : 0
  return { hz: sr / (best + shift), rms }
}

const toSemitone = (hz) => 12 * Math.log2(hz / 110)

const percentile = (sorted, p) => sorted[Math.min(sorted.length - 1, Math.max(0, Math.round(p * (sorted.length - 1))))]

// Melody range verdict. The thresholds are a coarse read of conversational
// pitch movement, not a clinical measure — copy using it must say "estimate".
export function summarise(semitones) {
  if (semitones.length < 40) return { verdict: 'unclear', rangeSt: 0, medianHz: 0 }
  const sorted = [...semitones].sort((x, y) => x - y)
  const rangeSt = percentile(sorted, 0.9) - percentile(sorted, 0.1)
  const medianHz = 110 * 2 ** (percentile(sorted, 0.5) / 12)
  const verdict = rangeSt < 4 ? 'flat' : rangeSt < 8 ? 'moderate' : 'expressive'
  return { verdict, rangeSt: Math.round(rangeSt * 10) / 10, medianHz: Math.round(medianHz) }
}

function syntheticPoint(t) {
  // Phrases of ~2.6 s separated by short pauses, each with a rise-fall shape,
  // like a sentence spoken with ordinary intonation.
  const phraseLen = 2.6
  const gap = 0.55
  const cycle = phraseLen + gap
  const k = Math.floor(t / cycle)
  const u = t - k * cycle
  if (u > phraseLen) return { st: null, lv: 0 }
  const x = u / phraseLen
  const seed = Math.sin(k * 12.9898) * 43758.5453
  const r = seed - Math.floor(seed)
  const arc = Math.sin(Math.PI * Math.min(1, x * 1.15)) * (3 + r * 3)
  const wiggle = 1.4 * Math.sin(t * 6.1 + r * 6) + 0.8 * Math.sin(t * 11.3 + k)
  const fall = x > 0.75 ? -(x - 0.75) * (r > 0.5 ? 10 : -6) : 0
  return { st: 2 + arc + wiggle + fall, lv: 0.35 + 0.35 * Math.sin(Math.PI * x) }
}

function draw(ctx, w, h, points, center, live) {
  ctx.clearRect(0, 0, w, h)
  const mid = h * 0.5
  const amp = h * 0.36

  // five-line staff
  ctx.lineWidth = 1
  ctx.strokeStyle = 'rgba(148, 163, 214, 0.09)'
  for (let i = -2; i <= 2; i++) {
    const y = Math.round(mid + i * amp * 0.42) + 0.5
    ctx.beginPath()
    ctx.moveTo(0, y)
    ctx.lineTo(w, y)
    ctx.stroke()
  }

  const step = w / (VISIBLE_SECONDS * FRAMES_PER_SEC)
  const headX = w * HEAD_X
  const yOf = (st) => mid - ((st - center) / ST_SPAN) * amp
  const grad = ctx.createLinearGradient(0, 0, headX, 0)
  grad.addColorStop(0, 'rgba(51, 135, 255, 0)')
  grad.addColorStop(0.35, 'rgba(51, 135, 255, 0.85)')
  grad.addColorStop(1, 'rgba(94, 224, 255, 1)')
  const glow = ctx.createLinearGradient(0, 0, headX, 0)
  glow.addColorStop(0, 'rgba(51, 135, 255, 0)')
  glow.addColorStop(1, 'rgba(94, 224, 255, 0.22)')

  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  const n = points.length
  for (const pass of [0, 1]) {
    ctx.strokeStyle = pass === 0 ? glow : grad
    for (let i = 1; i < n; i++) {
      const p0 = points[i - 1]
      const p1 = points[i]
      if (p0.st == null || p1.st == null) continue
      const x0 = headX - (n - i) * step
      if (x0 < -10) continue
      const base = 2.2 + p1.lv * 5
      ctx.lineWidth = pass === 0 ? base * 3.2 : base
      ctx.beginPath()
      ctx.moveTo(x0, yOf(p0.st))
      ctx.lineTo(x0 + step, yOf(p1.st))
      ctx.stroke()
    }
  }

  // cadence dot at the head
  let last = null
  for (let i = n - 1; i >= Math.max(0, n - BRIDGE_FRAMES * 3); i--) {
    if (points[i].st != null) { last = points[i]; break }
  }
  const headY = last ? yOf(last.st) : mid
  const lv = last ? last.lv : 0
  const r = 5 + lv * (live ? 7 : 4)
  const halo = ctx.createRadialGradient(headX, headY, 0, headX, headY, r * 3.2)
  halo.addColorStop(0, 'rgba(255, 107, 74, 0.45)')
  halo.addColorStop(1, 'rgba(255, 107, 74, 0)')
  ctx.fillStyle = halo
  ctx.beginPath()
  ctx.arc(headX, headY, r * 3.2, 0, Math.PI * 2)
  ctx.fill()
  ctx.fillStyle = last ? '#FF6B4A' : 'rgba(255, 107, 74, 0.55)'
  ctx.beginPath()
  ctx.arc(headX, headY, r, 0, Math.PI * 2)
  ctx.fill()
}

// status: 'idle' | 'requesting' | 'live' | 'done' | 'denied' | 'unsupported'
export function useVoiceContour({ maxSeconds = 8 } = {}) {
  // Callback ref: the canvas can mount after this hook (loading states,
  // analyzing screens), and the render loop must re-attach when it does.
  const [canvas, canvasRef] = useState(null)
  const [status, setStatus] = useState('idle')
  const [readout, setReadout] = useState({ hz: 0, level: 0, seconds: 0 })
  const [summary, setSummary] = useState(null)

  const s = useRef({
    points: [],
    center: 4,
    t0: performance.now(),
    raf: 0,
    audio: null, // { ctx, analyser, data, stream, ownsStream, startedAt }
    voiced: [],
    lastSt: null,
    smoothSt: null,
    unvoiced: 0,
    visible: true,
    lastReadout: 0,
    synthT: 0,
    maxSeconds,
  })

  const teardownAudio = useCallback(() => {
    const a = s.current.audio
    if (!a) return
    s.current.audio = null
    try { a.source.disconnect() } catch { /* already gone */ }
    if (a.ownsStream) a.stream.getTracks().forEach((t) => t.stop())
    a.ctx.close().catch(() => {})
  }, [])

  const finish = useCallback(() => {
    teardownAudio()
    setSummary(summarise(s.current.voiced))
    setStatus('done')
  }, [teardownAudio])

  // Render loop
  useEffect(() => {
    if (!canvas) return undefined
    const ctx = canvas.getContext('2d')
    const st = s.current
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const { width, height } = canvas.getBoundingClientRect()
      canvas.width = Math.max(1, Math.round(width * dpr))
      canvas.height = Math.max(1, Math.round(height * dpr))
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null
    ro?.observe(canvas)
    const io = typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(([e]) => { st.visible = e.isIntersecting })
      : null
    io?.observe(canvas)

    const maxPoints = Math.ceil(VISIBLE_SECONDS * FRAMES_PER_SEC * HEAD_X) + 4

    const pushPoint = (p) => {
      st.points.push(p)
      if (st.points.length > maxPoints) st.points.splice(0, st.points.length - maxPoints)
    }

    // Prefill with synthetic speech so the first paint is already a line
    if (!st.points.length) {
      for (let i = 0; i < maxPoints; i++) pushPoint(syntheticPoint(i / FRAMES_PER_SEC))
      st.synthT = maxPoints / FRAMES_PER_SEC
    }

    const tick = (now) => {
      st.raf = requestAnimationFrame(tick)
      if (!st.visible) return
      const { width, height } = canvas.getBoundingClientRect()
      const a = st.audio

      if (a) {
        a.analyser.getFloatTimeDomainData(a.data)
        const { hz, rms } = detectPitch(a.data, a.ctx.sampleRate)
        const level = Math.min(1, rms * 9)
        if (hz) {
          let semi = toSemitone(hz)
          // reject single-frame octave jumps
          if (st.smoothSt != null && Math.abs(semi - st.smoothSt) > 9) semi = st.smoothSt
          st.smoothSt = st.smoothSt == null ? semi : st.smoothSt + (semi - st.smoothSt) * 0.45
          st.voiced.push(st.smoothSt)
          st.unvoiced = 0
          st.lastSt = st.smoothSt
          pushPoint({ st: st.smoothSt, lv: level })
          // let the vertical centre drift to the speaker's own register
          st.center += (st.smoothSt - st.center) * 0.02
        } else {
          st.unvoiced += 1
          if (st.unvoiced <= BRIDGE_FRAMES && st.lastSt != null) {
            pushPoint({ st: st.lastSt, lv: level * 0.5 })
          } else {
            st.smoothSt = null
            pushPoint({ st: null, lv: 0 })
          }
        }
        const secs = (now - a.startedAt) / 1000
        if (now - st.lastReadout > 100) {
          st.lastReadout = now
          setReadout({ hz: hz ? Math.round(hz) : 0, level, seconds: secs })
        }
        if (Number.isFinite(st.maxSeconds) && secs >= st.maxSeconds) {
          // defer: finish() sets state and tears down the audio graph
          setTimeout(() => finish(), 0)
          st.maxSeconds = Infinity
        }
      } else if (!reduceMotion) {
        st.synthT += 1 / FRAMES_PER_SEC
        pushPoint(syntheticPoint(st.synthT))
        st.center += (4 - st.center) * 0.02
      }

      draw(ctx, width, height, st.points, st.center, !!a)
    }

    if (reduceMotion) {
      const { width, height } = canvas.getBoundingClientRect()
      draw(ctx, width, height, st.points, st.center, false)
    }
    st.raf = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(st.raf)
      ro?.disconnect()
      io?.disconnect()
    }
  }, [canvas, finish])

  useEffect(() => () => teardownAudio(), [teardownAudio])

  // Pass `stream` to draw from a stream the caller owns (e.g. while recording);
  // otherwise the mic is opened here and closed again on stop.
  const start = useCallback(async ({ stream, maxSeconds: limit } = {}) => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext
    if (!AudioCtx || (!stream && !navigator.mediaDevices?.getUserMedia)) {
      setStatus('unsupported')
      return false
    }
    teardownAudio()
    setSummary(null)
    try {
      let ownsStream = false
      if (!stream) {
        setStatus('requesting')
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: true },
        })
        ownsStream = true
      }
      const ctx = new AudioCtx()
      if (ctx.state === 'suspended') await ctx.resume()
      const source = ctx.createMediaStreamSource(stream)
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 2048
      source.connect(analyser)
      const st = s.current
      st.voiced = []
      st.smoothSt = null
      st.lastSt = null
      st.unvoiced = 0
      st.maxSeconds = limit === undefined ? maxSeconds : limit
      st.audio = { ctx, analyser, source, stream, ownsStream, data: new Float32Array(analyser.fftSize), startedAt: performance.now() }
      setReadout({ hz: 0, level: 0, seconds: 0 })
      setStatus('live')
      return true
    } catch (err) {
      setStatus(err?.name === 'NotAllowedError' || err?.name === 'SecurityError' ? 'denied' : 'unsupported')
      return false
    }
  }, [maxSeconds, teardownAudio])

  const stop = useCallback(() => {
    if (s.current.audio) finish()
  }, [finish])

  const reset = useCallback(() => {
    teardownAudio()
    setSummary(null)
    setStatus('idle')
  }, [teardownAudio])

  return { canvasRef, status, readout, summary, start, stop, reset }
}
